import React, { useState, useEffect, useRef } from 'react';
import { Send, X } from 'lucide-react';
import { OrnamentalBracket, DecorativeLine, MonasteryMark } from './MonasteryElements';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUGGESTED_PROMPTS = [
  "What makes Kloster Laks special?",
  "Tell me about the Bareksten Gin variety",
  "How is the salmon prepared?",
  "What's the monastic tradition connection?",
  "How can I order your products?",
  "What do Manchester City and Haaland say?"
];

const MonasticChat = () => {
  const [messages, setMessages] = useState([
    { 
      type: 'ai', 
      text: 'Welcome to Kloster Laks. I\'m your AI concierge, here to share our story of monastic traditions and help you discover our four exceptional varieties.\n\nHow may I assist you today?' 
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  };

  useEffect(() => {
    // Smooth scroll with a slight delay to prevent jumping
    // Only scroll if there are new messages (not on initial load)
    if (messages.length > 1) {
      const timer = setTimeout(() => {
        scrollToBottom();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [messages]);

  const handleSend = async (messageText = null) => {
    const textToSend = messageText || input.trim();
    if (!textToSend || isLoading) return;

    setInput('');
    
    // Add user message to UI
    const newUserMessage = { type: 'user', text: textToSend };
    setMessages(prev => [...prev, newUserMessage]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: textToSend,
        session_id: sessionId
      });

      // Add AI response to UI
      setMessages(prev => [...prev, { type: 'ai', text: response.data.response }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        type: 'ai', 
        text: 'I apologize for the inconvenience. Please contact Rune directly at rune@klosterlaks.no or +47 480 09 519 for immediate assistance.'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePromptClick = (prompt) => {
    handleSend(prompt);
  };

  const clearChat = () => {
    setMessages([
      { 
        type: 'ai', 
        text: 'Welcome to Kloster Laks. I\'m your AI concierge, here to share our story of monastic traditions and help you discover our four exceptional varieties.\n\nHow may I assist you today?' 
      }
    ]);
  };

  return (
    <section id="chat" className="relative border-t border-white/8" style={{ paddingTop: '22vh', paddingBottom: '22vh' }}>
      <div className="max-w-screen-xl mx-auto px-6 sm:px-8 lg:px-16">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-16 sm:mb-20 text-center scroll-reveal">
            <div className="flex items-center justify-center gap-3 mb-8">
              <OrnamentalBracket className="w-4 h-8 text-white" side="left" />
              <p className="text-[10px] sm:text-[11px] tracking-[0.28em] sm:tracking-[0.3em] text-gray-400 uppercase font-light">AI Concierge</p>
              <OrnamentalBracket className="w-4 h-8 text-white" side="right" />
            </div>
            <h2 className="text-[2.75rem] sm:text-[3.75rem] md:text-[4.5rem] font-light leading-[0.98] mb-6">Ask Us Anything</h2>
            <DecorativeLine className="w-24 sm:w-32 h-4 mx-auto mb-8 text-white" />
            <p className="text-sm sm:text-base text-gray-200 font-light max-w-2xl mx-auto leading-[1.8]">
              Our AI concierge knows everything about our monastic traditions, varieties, and craftsmanship.
            </p>
          </div>

          {/* Chat Container */}
          <div className="scroll-reveal">
            <div className="border border-white/8 bg-gradient-to-b from-black/60 to-black/40 backdrop-blur-md hover:border-white/12 transition-all duration-700 overflow-hidden">
              {/* Messages Area */}
              <div ref={chatContainerRef} className="p-6 sm:p-8 lg:p-10 overflow-y-auto scroll-smooth" style={{ minHeight: '450px', maxHeight: '550px' }}>
                {messages.length <= 1 && (
                  <div className="mb-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      {SUGGESTED_PROMPTS.map((prompt, index) => (
                        <button
                          key={index}
                          onClick={() => handlePromptClick(prompt)}
                          disabled={isLoading}
                          className="text-left px-4 sm:px-5 py-3 sm:py-4 border border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20 transition-all duration-300 group disabled:opacity-50"
                        >
                          <p className="text-xs sm:text-sm text-gray-300 font-light group-hover:text-gray-100 transition-colors leading-relaxed">{prompt}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="space-y-5 sm:space-y-6">
                  {messages.map((msg, index) => (
                    <div key={index} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-full sm:max-w-[85%] ${
                        msg.type === 'user' 
                          ? 'bg-white/12 border-white/25' 
                          : 'bg-white/5 border-white/10'
                      } border px-5 sm:px-6 py-4 sm:py-5 hover:border-white/20 transition-all duration-300`}>
                        {msg.type === 'ai' && (
                          <div className="flex items-center gap-2 mb-3">
                            <MonasteryMark className="w-4 h-4 text-white opacity-50" />
                            <p className="text-[10px] text-gray-400 uppercase tracking-[0.25em]">Concierge</p>
                          </div>
                        )}
                        <p className="text-sm sm:text-base text-gray-50 font-light leading-[1.85] whitespace-pre-wrap">{msg.text}</p>
                      </div>
                    </div>
                  ))}
                  {isLoading && (
                    <div className="flex justify-start">
                      <div className="bg-white/5 border border-white/10 px-6 py-5">
                        <div className="flex items-center gap-3">
                          <MonasteryMark className="w-4 h-4 text-white opacity-40 animate-pulse" />
                          <div className="flex gap-1.5">
                            <div className="w-2 h-2 bg-white/40 rounded-full animate-pulse"></div>
                            <div className="w-2 h-2 bg-white/40 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                            <div className="w-2 h-2 bg-white/40 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </div>

              {/* Input Area */}
              <div className="border-t border-white/8 p-5 sm:p-6 lg:p-8 bg-black/20">
                {messages.length > 1 && (
                  <div className="flex justify-between items-center mb-4 sm:mb-5">
                    <p className="text-[10px] sm:text-xs text-gray-500 font-light">{messages.length - 1} message{messages.length - 1 !== 1 ? 's' : ''}</p>
                    <button
                      onClick={clearChat}
                      className="text-[10px] sm:text-xs text-gray-500 hover:text-white transition-colors flex items-center gap-2 font-light"
                    >
                      <X className="w-3 h-3" />
                      <span className="hidden sm:inline">Clear conversation</span>
                    </button>
                  </div>
                )}
                <form onSubmit={(e) => { e.preventDefault(); handleSend(); }} className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Type your question..."
                    className="flex-1 px-5 sm:px-6 py-4 sm:py-5 bg-black/60 border border-white/15 text-white focus:border-white transition-all duration-500 text-sm sm:text-base font-light placeholder-gray-500"
                    disabled={isLoading}
                    autoComplete="off"
                  />
                  <button
                    type="submit"
                    disabled={isLoading || !input.trim()}
                    className="w-full sm:w-auto px-6 sm:px-8 py-4 sm:py-5 border border-white/25 hover:bg-white hover:text-black transition-all duration-500 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="text-xs tracking-[0.25em] uppercase font-light">Send</span>
                  </button>
                </form>
                <p className="text-[10px] sm:text-xs text-gray-600 mt-4 sm:mt-5 text-center font-light leading-relaxed">
                  AI-powered by GPT-4o • For partnership or bulk orders: rune@klosterlaks.no
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MonasticChat;
